import { useEffect } from 'react';
import { SOGLIA_SENTINELLA_MS } from '../constants/timing';
import { log } from '../utils/logger';
import { segnalaDesync } from '../utils/diagnosi-sincronia';

/**
 * La sentinella di una damiera ferma.
 *
 * Il caso da cui nasce: si muove, l'avversario risponde, ma il `game:update`
 * con la sua mossa non arriva mai — perso per strada, o arrivato a un socket
 * che nel frattempo era fuori dalla stanza. La damiera resta alla posizione di
 * prima e il proprio orologio continua a scorrere, perche' `game:clock-sync`
 * (1 Hz, stessa stanza) dice che il tratto e' tornato a noi. Da li' non si esce
 * da soli: e' il nostro turno, quindi nessun altro evento cambiera' piu' la
 * damiera, e il watchdog che esiste gia' in `OnlineGame` guarda solo gli stati
 * NON giocabili — qui lo stato e' `'playing'`, e sembra tutto a posto.
 *
 * Due inneschi, una sola azione, nessuno dei due inventato: sono due
 * contraddizioni.
 *
 *  1. **Eco che non torna.** Una mossa ottimistica spedita e mai confermata.
 *     Vale anche senza orologio.
 *  2. **Orologio contro damiera.** `activePlayer` e il tratto della posizione
 *     vengono ENTRAMBI dal server e non possono discordare. Discordano per una
 *     frazione di secondo dopo la propria mossa — la damiera locale e' avanti,
 *     l'orologio aspetta l'eco — e mai per sei.
 *
 * ⚠️ RIPETUTA, non un colpo solo: la gemella di `OnlineGame` porta gia' la
 * lezione («staccata la rete per due minuti, socket.io esaurisce i suoi quindici
 * tentativi e si ferma per sempre»). Finche' la contraddizione dura, si
 * richiede.
 */
export function useGuardiaSincronia(params: {
  /** Si guarda solo una partita viva a schermo. */
  attiva: boolean;
  /** Di chi e' il tratto secondo la DAMIERA. */
  turnoDamiera: 'white' | 'black' | null;
  /** Di chi e' il tratto secondo l'OROLOGIO del server (`null` = fermo). */
  turnoOrologio: 'white' | 'black' | null;
  /** C'e' una mossa nostra spedita e non ancora confermata. */
  mossaInVolo: boolean;
  /** Che cosa si fa quando si e' persa una mossa: richiedere la verita'. */
  resync: () => void;
  /** Da dove, per la diagnosi. */
  contesto: 'online' | 'arena' | 'torneo';
}): void {
  const { attiva, turnoDamiera, turnoOrologio, mossaInVolo, resync, contesto } = params;

  const orologioControDamiera =
    turnoOrologio !== null && turnoDamiera !== null && turnoOrologio !== turnoDamiera;
  const contraddizione = attiva && (mossaInVolo || orologioControDamiera);

  useEffect(() => {
    if (!contraddizione) return;
    const id = window.setInterval(() => {
      log.warn('GuardiaSincronia', 'damiera ferma: chiedo il resync', { contesto, mossaInVolo, turnoDamiera, turnoOrologio });
      segnalaDesync('sentinella-resync', { contesto, mossaInVolo, turnoDamiera, turnoOrologio });
      resync();
    }, SOGLIA_SENTINELLA_MS);
    return () => window.clearInterval(id);
    // `contraddizione` e' la sola dipendenza che deve riarmare il conto: quando
    // la mossa arriva diventa falsa, l'effetto si smonta e il timer muore prima
    // di scattare. I tre valori qui sotto sono solo per la diagnosi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contraddizione, resync]);
}
